import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { Backdrop, Fade } from "@mui/material";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useDialog } from "app/hook";
import { AppDispatch, RootState } from "app/store";
import { DialogType, RestrictedFeatureSource } from "app/types";
import I18n from "common/i18n";
import { usePlaySound, useScale } from "common/utils";
import { CloseBtn, DialogMessage } from "common/elements";
import { PlanType } from "features/subscription/types";
import { updateAiChatUsage } from "features/subscription";
import { usePlan, usePlanLimits } from "features/subscription/hooks";
import { Message, MessageRole } from "./types";
import { getAIChatHistory, sendMessageSSE } from "./api";
import { ChatHeader, ChatInput, MessageArea } from "./components";
import {
  actions,
  selectCurrentSessionId,
  selectError,
  selectIsTyping,
  selectMessages,
} from "./assistantSlice";

export const AssistantDialog = () => {
  const play = usePlaySound();
  const { scale } = useScale();
  const dispatch = useDispatch<AppDispatch>();
  const handleDialog = useDialog();
  const plan = usePlan();
  const limits = usePlanLimits();
  const assistant = useSelector((state: RootState) => state.config.dialog.assistant);
  const active = useSelector((state: RootState) => state.user.active);
  const aiChatEnabled = useSelector((state: RootState) => state.config.userConfig.ai_chat);
  const messages = useSelector(selectMessages);
  const isTyping = useSelector(selectIsTyping);
  const currentSessionId = useSelector(selectCurrentSessionId);
  const generalError = useSelector(selectError);
  const greetingShown = useRef(false);
  const isNewChat = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // 使用状況（Redux storeから取得）
  const usageInfo = {
    used: limits.aiChatDaily.used,
    remaining: limits.aiChatDaily.remaining,
    limit: limits.aiChatDaily.limit,
    isUnlimited: limits.aiChatDaily.unlimited,
  };

  // 制限エラーのstate
  const [limitError, setLimitError] = useState<string | null>(null);
  const [showNewChatConfirm, setShowNewChatConfirm] = useState(false);

  // 履歴取得用の型
  type HistoryData = Awaited<ReturnType<typeof getAIChatHistory>>;

  // 履歴取得（ページング対応）
  const {
    data: historyData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingHistory,
  } = useInfiniteQuery<HistoryData, Error, { pages: HistoryData[]; pageParams: unknown[] }>({
    queryKey: ["ai-chat-history", active?.uid],
    queryFn: ({ pageParam }) =>
      getAIChatHistory({
        uid: active?.uid || "",
        beforeSessionId: pageParam as string | undefined,
      }),
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    initialPageParam: undefined,
    enabled: assistant.value && !!active?.uid,
    staleTime: 1000 * 60 * 5,
  });

  // 過去のセッション一覧（フラット化）
  const historySessions = historyData?.pages.flatMap((page) => page.sessions) || [];

  // 履歴取得後、最新セッションのIDをcurrentSessionIdにセット
  useEffect(() => {
    if (!historyData || currentSessionId || isNewChat.current) return;
    const allSessions = historyData.pages.flatMap((p) => p.sessions);
    if (allSessions.length === 0) return;
    const latestSession = allSessions[allSessions.length - 1];
    dispatch(actions.setCurrentSession(latestSession.id));
    greetingShown.current = true;
  }, [historyData, currentSessionId, dispatch]);

  // ダイアログが開いた時に制限チェック、閉じたらリセット
  useEffect(() => {
    if (!assistant.value) return;

    if (
      (plan === PlanType.TRIAL || plan === PlanType.FREE) &&
      !usageInfo.isUnlimited &&
      usageInfo.remaining <= 0
    ) {
      setLimitError(I18n.t("MSG_ASSISTANT_LIMIT_REACHED"));
    }

    return () => {
      setLimitError(null);
    };
  }, [assistant.value, plan, usageInfo.isUnlimited, usageInfo.remaining]);

  // ダイアログが開いた時にローカルで挨拶メッセージを表示（DBには保存しない）
  useEffect(() => {
    if (!assistant.value || greetingShown.current || isLoadingHistory) return;
    if (messages.length > 0 || historySessions.length > 0) return;

    greetingShown.current = true;

    if (!aiChatEnabled) {
      setLimitError(I18n.t("MSG_ASSISTANT_PARENT_NOT_ALLOWED"));
      return;
    }

    const greeting: Message = {
      id: `greeting-${Date.now()}`,
      sessionId: "",
      role: MessageRole.ASSISTANT,
      content: I18n.t("MSG_ASSISTANT_GREETING", { name: active?.display_name || "" }),
      createdAt: dayjs().toISOString(),
    };
    dispatch(actions.addMessage(greeting));
  }, [
    assistant.value,
    messages.length,
    dispatch,
    aiChatEnabled,
    plan,
    active?.display_name,
    isLoadingHistory,
    historySessions.length,
  ]);

  // 上スクロールでページング
  const handleScrollTop = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleClose = () => {
    play();
    handleDialog({ type: DialogType.ASSISTANT, value: false });
  };

  const handleNewChat = useCallback(() => {
    play();
    setShowNewChatConfirm(true);
  }, [play]);

  const handleNewChatConfirm = useCallback(() => {
    setShowNewChatConfirm(false);
    isNewChat.current = true;
    greetingShown.current = true;
    dispatch(actions.setCurrentSession(null));

    const greeting: Message = {
      id: `greeting-${Date.now()}`,
      sessionId: "",
      role: MessageRole.ASSISTANT,
      content: I18n.t("MSG_ASSISTANT_GREETING", { name: active?.display_name || "" }),
      createdAt: dayjs().toISOString(),
    };
    dispatch(actions.addMessage(greeting));
  }, [dispatch, active?.display_name]);

  const handleSendMessage = useCallback(
    async (content: string, imageUrls?: string[]) => {
      if (!active?.uid) return;

      if (
        (plan === PlanType.TRIAL || plan === PlanType.FREE) &&
        !usageInfo.isUnlimited &&
        usageInfo.used >= usageInfo.limit
      ) {
        handleDialog({
          type: DialogType.RESTRICTED_FEATURE,
          value: true,
          args: { source: RestrictedFeatureSource.AI_CHAT_LIMIT },
        });
        return;
      }

      const userMessage: Message = {
        id: `msg-${Date.now()}`,
        sessionId: currentSessionId || "",
        role: MessageRole.USER,
        content,
        images: imageUrls?.map((url) => ({ url })),
        createdAt: dayjs().toISOString(),
      };
      dispatch(actions.addMessage(userMessage));
      dispatch(actions.setIsTyping(true));
      dispatch(actions.setError(null));

      abortControllerRef.current = new AbortController();

      try {
        await sendMessageSSE({
          uid: active.uid,
          sessionId: currentSessionId || "new",
          message: content,
          imageUrls,
          pageUrl: window.location.href,
          signal: abortControllerRef.current.signal,
          callbacks: {
            onMessageStart: (messageId, sessionId) => {
              if (!currentSessionId) {
                dispatch(actions.setCurrentSession(sessionId));
                isNewChat.current = false;
              }
              const aiMessage: Message = {
                id: messageId,
                sessionId,
                role: MessageRole.ASSISTANT,
                content: "",
                createdAt: dayjs().toISOString(),
              };
              dispatch(actions.addMessage(aiMessage));
            },
            onContentDelta: (delta) => {
              dispatch(actions.updateLastMessage(delta));
            },
            onMessageEnd: (_messageId, usage) => {
              dispatch(actions.setIsTyping(false));
              abortControllerRef.current = null;
              if (usage && active?.uid) {
                dispatch(
                  updateAiChatUsage({
                    uid: active.uid,
                    used: usage.used,
                    remaining: usage.remaining,
                  }),
                );
              }
            },
            onError: (code, message) => {
              dispatch(actions.setIsTyping(false));
              dispatch(actions.setError(message));
              dispatch(actions.removeLastEmptyAssistantMessage());
              console.error(`AI Chat Error [${code}]:`, message);
              abortControllerRef.current = null;
              if (
                code === "RATE_LIMIT_EXCEEDED" ||
                code === "PLAN_NOT_ALLOWED" ||
                code === "QUOTA_EXCEEDED" ||
                code === "ACCESS_DENIED"
              ) {
                setLimitError(message);
              }
            },
            onSessionReset: (newSessionId) => {
              const oldSessionId = currentSessionId || "";
              dispatch(actions.updateMessagesSessionId({ oldSessionId, newSessionId }));
              dispatch(actions.setCurrentSession(newSessionId));
            },
          },
        });
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          dispatch(actions.setIsTyping(false));
        } else {
          dispatch(actions.setIsTyping(false));
          dispatch(actions.setError("メッセージの送信に失敗しました"));
          console.error("Failed to send message:", err);
        }
        abortControllerRef.current = null;
      }
    },
    [
      active?.uid,
      currentSessionId,
      dispatch,
      plan,
      handleDialog,
      usageInfo.used,
      usageInfo.remaining,
      usageInfo.limit,
      usageInfo.isUnlimited,
      aiChatEnabled,
    ],
  );

  const handleStop = useCallback(() => {
    play();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    dispatch(actions.removeLastEmptyAssistantMessage());
    dispatch(actions.setIsTyping(false));
  }, [dispatch, play]);

  return (
    <>
      <Backdrop
        open={assistant.value}
        sx={{
          zIndex: 999999,
          backgroundColor: "transparent",
          opacity: 1,
          backdropFilter: "blur(8px)",
        }}
      >
        <Fade in>
          <div className="absolute inset-0 flex-col-el flex-center bg-textcolor-black/50 pointer-events-auto">
            <div className="flex-col-center" style={{ transform: `scale(${scale})` }}>
              {/* モーダルコンテナ */}
              <div className="relative w-[1100px] h-[800px] border-[6px] border-white rounded-[16px] bg-white shadow-lg">
                <CloseBtn close={handleClose} />

                {/* モーダルコンテンツ */}
                <div className="w-full h-full rounded-[10px] bg-beige-60 overflow-hidden flex flex-col">
                  <ChatHeader
                    usageInfo={
                      (plan === PlanType.TRIAL || plan === PlanType.FREE) && !usageInfo.isUnlimited
                        ? usageInfo
                        : null
                    }
                    onNewChat={handleNewChat}
                    isTyping={isTyping}
                  />
                  {/* 制限エラー表示 */}
                  {limitError && (
                    <div className="flex-row-el justify-center items-center gap-4 py-3 bg-orange-100">
                      <p className="text text-sm text-white">{limitError}</p>
                      <button
                        className="flex-row-el items-center gap-1 px-3 py-1 bg-safe text-white rounded-full text-sm cursor-pointer active:opacity-70 transition-colors border-0"
                        onClick={() => {
                          play();
                          handleDialog({ type: DialogType.UPGRADE_PROMOTION, value: true });
                        }}
                      >
                        <span className="material-symbols-outlined !text-base">sync_alt</span>
                        {I18n.t("MSG_ASSISTANT_UPGRADE_BTN")}
                      </button>
                    </div>
                  )}
                  {/* 一般エラー表示（AI_ERROR等） */}
                  {generalError && !limitError && (
                    <div className="flex-row-el justify-center items-center gap-2 py-2 bg-red-100">
                      <span className="material-symbols-outlined !text-base text-white">error</span>
                      <p className="text text-sm text-white">{I18n.t("MSG_ASSISTANT_AI_ERROR")}</p>
                    </div>
                  )}
                  {/* 会話エリア + 入力エリア一体化コンテナ */}
                  <div className="flex-1 flex flex-col mx-4 my-4 bg-white rounded-xl overflow-hidden">
                    <MessageArea
                      messages={messages}
                      isTyping={isTyping}
                      currentSessionId={currentSessionId}
                      userIcon={active?.icon}
                      historySessions={historySessions}
                      isLoadingHistory={isLoadingHistory}
                      isFetchingNextPage={isFetchingNextPage}
                      onScrollTop={handleScrollTop}
                    />
                    <ChatInput
                      onSend={handleSendMessage}
                      onStop={handleStop}
                      isTyping={isTyping}
                      disabled={!aiChatEnabled}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Fade>
      </Backdrop>

      <DialogMessage
        open={showNewChatConfirm}
        zIndex={9999999}
        title={I18n.t("MSG_ASSISTANT_NEW_CHAT_CONFIRM_TITLE")}
        message={I18n.t("MSG_ASSISTANT_NEW_CHAT_CONFIRM_MESSAGE")}
        close={() => setShowNewChatConfirm(false)}
        confirm={handleNewChatConfirm}
        confirmBtnText={I18n.t("MSG_ASSISTANT_NEW_CHAT_CONFIRM_BTN")}
        confirmBtnIcon="add_comment"
      />
    </>
  );
};
